import { useEffect, useState } from "react";
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  MenuIcon,
} from "@heroicons/react/outline";
import PageLoader from "../../../components/pageLoader";
import {
  createTicketTypes,
  deleteTicketType,
  getAllTicketTypesByEvent,
  getTicketTypesByEvent,
  updateTicketType,
} from "../../../api/ticketTypesApi";
import ToastContainer from "../../../components/toastContainer";
import Toast from "../../../components/toast";
import TicketFormModal from "./TicketsFormModal";
import DeleteConfirmationModal from "../../../components/deleteModal";
import { CURRENCY_MAP } from "../../../constants";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const TicketTypesTab = ({ eventId }) => {
  const [showAddModal, setShowAddModal] = useState(false);

  const [limit, setLimit] = useState(10);

  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const [allTickets, setAllTickets] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [deleteTicketId, setDeleteTicketId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const totalPages = Math.ceil(total / limit);

  const [editingTicket, setEditingTicket] = useState(null);
  const [saving, setSaving] = useState(false);

  const [toast, setToast] = useState(null);

  // ── DND ──
  const sensors = useSensors(useSensor(PointerSensor));

  const getPageNumbers = () => {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  };

  const formatCurrency = (amount, currency) => {
    const symbol = CURRENCY_MAP[currency] || currency;
    return `${symbol} ${amount}`;
  };

  const handleTicketSubmit = async (payload) => {
    try {
      setSaving(true);

      if (editingTicket) {
        await updateTicketType(editingTicket.id, payload);
      } else {
        await createTicketTypes(payload);
      }

      setEditingTicket(null);
      setShowAddModal(false);

      fetchTickets();
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTicket = async () => {
    try {
      setDeleting(true);
      await deleteTicketType(deleteTicketId);

      setDeleteTicketId(null);

      // Refresh users list
      fetchTickets();
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setDeleting(false);
    }
  };

  // ── DRAG END ──
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = tickets.findIndex((t) => t.id === active.id);
    const newIndex = tickets.findIndex((t) => t.id === over.id);
    const reordered = arrayMove(tickets, oldIndex, newIndex);

    // Optimistically update UI
    setTickets(reordered);

    // Patch only tickets whose sort_order changed
    try {
      await Promise.all(
        reordered.map((ticket, index) => {
          const newSortOrder = index + 1;
          if (ticket.sort_order !== newSortOrder) {
            return updateTicketType(ticket.id, {
              sort_order: newSortOrder,
              event: eventId,
            });
          }
          return Promise.resolve();
        }),
      );
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
      // Revert to server state on error
      fetchTickets();
    }
  };

  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError("");

      const allData = await getAllTicketTypesByEvent(999, 0, eventId);
      setAllTickets(allData.results || []);

      const offset = (page - 1) * limit;
      const data = await getTicketTypesByEvent(limit, offset, eventId);
      console.log("Fetched tickets:", data.results);
      // Sort by sort_order after fetching
      setTickets(
        (data.results || []).sort((a, b) => a.sort_order - b.sort_order),
      );
      setTotal(data.count || 0);
    } catch (err) {
      setError("Failed to load ticket types.");
    } finally {
      setLoading(false);
    }
  };

  const handleLimitChange = (e) => {
    setPage(1);
    setLimit(Number(e.target.value));
  };

  useEffect(() => {
    fetchTickets();
  }, [page, limit]);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-m font-semibold text-white">Ticket Types</h3>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
        >
          <PlusIcon className="w-4 h-4" />
          Add Ticket Category
        </button>
      </div>

      {/* Table */}
      <div className="bg-slate-900/60 border border-white/10 rounded-xl">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <div className="overflow-x-auto">
            {/* DndContext lives OUTSIDE the table to avoid invalid HTML nesting */}

            <table
              className="w-full border-collapse"
              style={{ minWidth: "1000px" }}
            >
              <thead>
                <tr className="border-b border-white/10 text-center text-xs uppercase tracking-wider text-slate-400">
                  <th className="px-2 py-4 w-6"></th>
                  <th className="px-3 py-4">Category Name</th>
                  <th className="px-3 py-4">Type</th>
                  <th className="px-3 py-4">Series</th>
                  <th className="px-3 py-4">Reserved</th>
                  <th className="px-3 py-4">Sold</th>
                  <th className="px-3 py-4">Available</th>
                  <th className="px-3 py-4">Price (Comm.)</th>
                  <th className="px-3 py-4">Income</th>
                  <th className="px-3 py-4">Commission</th>
                  <th className="px-3 py-4">Status</th>
                  <th className="px-3 py-4 text-right">Actions</th>
                </tr>
              </thead>

              <SortableContext
                items={tickets.map((t) => t.id)}
                strategy={verticalListSortingStrategy}
              >
                <tbody>
                  {tickets.map((tk) => (
                    <SortableRow
                      key={tk.id}
                      tk={tk}
                      formatCurrency={formatCurrency}
                      onEdit={() => setEditingTicket(tk)}
                      onDelete={() => setDeleteTicketId(tk.id)}
                    />
                  ))}
                </tbody>
              </SortableContext>
            </table>
          </div>
        </DndContext>
        {/* Empty state placeholder (future) */}
        {tickets.length === 0 && (
          <div className="p-8 text-center text-slate-400">
            No tickets found.
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <div className="flex items-center justify-between px-6 py-4 text-sm text-slate-400">
          <div className="flex items-center justify-center gap-3 px-6 py-4 text-sm text-slate-400">
            {/* ── Rows per page dropdown ── */}
            <div className="flex items-center gap-2">
              <span className="text-slate-400 text-sm">Rows per page:</span>
              <select
                value={limit}
                onChange={handleLimitChange}
                className="bg-slate-800 text-slate-300 border border-white/10 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-pink-500 cursor-pointer"
              >
                {[5, 10, 25, 50].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            {totalPages > 1 && (
              <>
                {/* Previous */}
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className={`px-3 py-1 rounded-md border border-white/10 transition
        ${
          page === 1
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-slate-800 text-white"
        }`}
                >
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="flex gap-1">
                  {getPageNumbers().map((pageNumber) => (
                    <button
                      key={pageNumber}
                      onClick={() => setPage(pageNumber)}
                      className={`px-3 py-1 rounded-md border border-white/10 transition
            ${
              page === pageNumber
                ? "bg-pink-600 text-white"
                : "text-slate-300 hover:bg-slate-800 hover:text-white"
            }`}
                    >
                      {pageNumber}
                    </button>
                  ))}
                </div>

                {/* Next */}
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className={`px-3 py-1 rounded-md border border-white/10 transition
        ${
          page === totalPages
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-slate-800 text-white"
        }`}
                >
                  Next
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <TicketFormModal
        isOpen={showAddModal || !!editingTicket}
        onClose={() => {
          setShowAddModal(false);
          setEditingTicket(null);
        }}
        onSubmit={handleTicketSubmit}
        loading={saving}
        mode={editingTicket ? "edit" : "create"}
        initialData={editingTicket}
        ticketTypes={allTickets}
        eventId={eventId}
      />

      <DeleteConfirmationModal
        isOpen={!!deleteTicketId}
        description="Are you sure you want to delete this ticket type?  This action cannot be undone."
        onClose={() => setDeleteTicketId(null)}
        onConfirm={handleDeleteTicket}
        loading={deleting}
      />

      <ToastContainer>
        {toast && (
          <div className="toast-enter">
            <Toast
              type={toast.type}
              message={toast.message}
              onClose={() => setToast(null)}
            />
          </div>
        )}
      </ToastContainer>
    </div>
  );
};

export default TicketTypesTab;

/* ── SORTABLE ROW ── */

function SortableRow({ tk, formatCurrency, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tk.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? "rgba(30,41,59,0.8)" : undefined,
    zIndex: isDragging ? 50 : undefined,
    position: isDragging ? "relative" : undefined,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="border-b text-center border-white/5 last:border-none hover:bg-slate-800/40 transition"
    >
      {/* DRAG HANDLE */}
      <td className="px-3 py-4 w-8">
        <button
          {...attributes}
          {...listeners}
          className="text-slate-500 hover:text-slate-300 cursor-grab active:cursor-grabbing transition"
          title="Drag to reorder"
        >
          <MenuIcon className="w-4 h-4" />
        </button>
      </td>

      <td className="px-3 py-4 text-sm text-slate-300">{tk.name}</td>

      <td className="px-3 py-4">
        <div className="text-sm font-medium text-white">{tk.type}</div>
      </td>

      <td className="px-3 py-4 text-sm text-slate-300">{tk.series}</td>

      <td className="px-3 py-4 text-sm text-slate-300">
        {tk.quantity_reserved || "-"}
      </td>

      <td className="px-3 py-4 text-sm text-slate-300">
        {tk.quantity_paid || "-"}
      </td>

      <td className="px-3 py-4 text-sm text-slate-300">
        {tk.quantity_available || "-"}
      </td>

      <td className="px-3 py-4 text-sm text-slate-300">
        {`${formatCurrency(tk.price, tk.currency)} (${tk.commission_amount})`}
      </td>

      <td className="px-3 py-4 text-sm text-slate-300">
        {formatCurrency(tk.total_income, tk.currency)}
      </td>

      <td className="px-3 py-4 text-sm text-slate-300">
        {formatCurrency(tk.total_commission, tk.currency)}
      </td>

      <td className="px-3 py-4 text-sm text-slate-300">
        {tk.display_status || "-"}
      </td>

      <td className="px-3 py-4 text-right">
        <div className="flex justify-end gap-2">
          <button
            onClick={onEdit}
            className="p-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-700 transition"
          >
            <PencilIcon className="w-4 h-4" />
          </button>

          <button
            onClick={onDelete}
            className="p-2 rounded-md text-slate-300 hover:text-red-400 hover:bg-red-500/10 transition"
            title="Delete user"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}
